import clsx from 'clsx';
import { AlertTriangle, Cpu, Database, Eye, Layers, ShieldAlert } from 'lucide-react';
import { formatCount, formatNumber } from '../../ndt/utils/format';
import { glossaryText } from '../utils/weldFieldGlossary';
import { DashboardMetricCard } from './dashboard/DashboardMetricCard';
import { AnalyticsDataTable, type AnalyticsColumn } from './shared/AnalyticsDataTable';
import { ColumnHeaderHelp } from './shared/ColumnHeaderHelp';

type AnomalyMetrics = Record<string, unknown> | null;

function metricNum(metrics: AnomalyMetrics, key: string): number | null {
  if (!metrics) return null;
  const val = metrics[key];
  if (typeof val === 'number' && Number.isFinite(val)) return val;
  if (typeof val === 'string' && val.trim() !== '') {
    const n = Number(val);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function formatMetricCount(metrics: AnomalyMetrics, key: string): string {
  const n = metricNum(metrics, key);
  return n != null ? formatCount(n) : '—';
}

function formatMetricPct(metrics: AnomalyMetrics, key: string): string {
  const n = metricNum(metrics, key);
  return n != null ? `${formatNumber(n, 1)}%` : '—';
}

interface OverlapCardProps {
  label: string;
  helpKey: string;
  value: string;
  highlight?: boolean;
}

function OverlapCard({ label, helpKey, value, highlight = false }: OverlapCardProps) {
  return (
    <div
      className={clsx(
        'rounded-xl border bg-page-bg/40 p-4',
        highlight ? 'border-[#E6A100]/40 bg-[rgba(230,161,0,0.06)]' : 'border-[#E2E5EA]',
      )}
    >
      <p className="text-xs font-semibold uppercase tracking-widest text-heritage-grey">
        <ColumnHeaderHelp label={label} help={glossaryText(helpKey)} />
      </p>
      <p
        className={clsx(
          'mt-1 text-2xl font-semibold',
          highlight ? 'text-[#B38600]' : 'text-blueprint-blue',
        )}
      >
        {value}
      </p>
    </div>
  );
}

interface ProxyRow {
  level: string;
  tp: string;
  fp: string;
  fn: string;
  precision: string;
  recall: string;
}

interface AnomalyMetricsPanelProps {
  metrics: AnomalyMetrics;
}

export const AnomalyMetricsPanel = ({ metrics }: AnomalyMetricsPanelProps) => {
  if (!metrics || Object.keys(metrics).length === 0) {
    return (
      <div className="flex items-center justify-center rounded-xl border border-dashed border-[#E2E5EA] bg-page-bg/30 py-12">
        <p className="text-sm text-heritage-grey">Anomaly metrics not available for this pipeline run.</p>
      </div>
    );
  }

  const hiddenCount = metricNum(metrics, 'num_only_per_system');

  const proxyRows: ProxyRow[] = [
    {
      level: 'Pass',
      tp: formatMetricCount(metrics, 'pass_proxy_tp'),
      fp: formatMetricCount(metrics, 'pass_proxy_fp'),
      fn: formatMetricCount(metrics, 'pass_proxy_fn'),
      precision: formatMetricPct(metrics, 'pass_proxy_precision_pct'),
      recall: formatMetricPct(metrics, 'pass_proxy_recall_pct'),
    },
    {
      level: 'Joint',
      tp: formatMetricCount(metrics, 'joint_proxy_tp'),
      fp: formatMetricCount(metrics, 'joint_proxy_fp'),
      fn: formatMetricCount(metrics, 'joint_proxy_fn'),
      precision: formatMetricPct(metrics, 'joint_proxy_precision_pct'),
      recall: formatMetricPct(metrics, 'joint_proxy_recall_pct'),
    },
  ];

  const proxyColumns: AnalyticsColumn<ProxyRow>[] = [
    {
      id: 'level',
      header: (
        <ColumnHeaderHelp label="Level" help={glossaryText('anomaly_proxy_level')} />
      ),
      cell: (r) => <span className="font-medium text-blueprint-blue">{r.level}</span>,
    },
    {
      id: 'tp',
      header: <ColumnHeaderHelp label="Both flagged" help={glossaryText('anomaly_proxy_tp')} />,
      cell: (r) => r.tp,
    },
    {
      id: 'fp',
      header: <ColumnHeaderHelp label="ML only" help={glossaryText('anomaly_proxy_fp')} />,
      cell: (r) => (
        <span className={r.fp !== '—' && r.fp !== '0' ? 'font-medium text-readable-red' : ''}>
          {r.fp}
        </span>
      ),
    },
    {
      id: 'fn',
      header: <ColumnHeaderHelp label="WPS only" help={glossaryText('anomaly_proxy_fn')} />,
      cell: (r) => r.fn,
    },
    {
      id: 'precision',
      header: (
        <ColumnHeaderHelp label="ML match %" help={glossaryText('anomaly_proxy_precision')} />
      ),
      cell: (r) => r.precision,
    },
    {
      id: 'recall',
      header: <ColumnHeaderHelp label="WPS catch %" help={glossaryText('anomaly_proxy_recall')} />,
      cell: (r) => r.recall,
    },
  ];

  return (
    <div className="space-y-5">
      <div>
        <h4 className="mb-3 text-sm font-semibold text-blueprint-blue">Detection overview</h4>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <DashboardMetricCard
            label={
              <ColumnHeaderHelp
                label="Total passes scored"
                help={glossaryText('total_welds')}
              />
            }
            value={formatMetricCount(metrics, 'total_welds')}
            icon={Database}
            accent="blue"
          />
          <DashboardMetricCard
            label={
              <ColumnHeaderHelp
                label="WPS failures"
                help={glossaryText('num_rule_based')}
              />
            }
            value={formatMetricCount(metrics, 'num_rule_based')}
            icon={ShieldAlert}
            accent="red"
          />
          <DashboardMetricCard
            label={
              <ColumnHeaderHelp
                label="ML-flagged passes"
                help={glossaryText('num_per_system')}
              />
            }
            value={formatMetricCount(metrics, 'num_per_system')}
            icon={Cpu}
            accent="blue"
          />
          <DashboardMetricCard
            label={
              <ColumnHeaderHelp
                label="Hidden issues"
                help={glossaryText('num_only_per_system')}
              />
            }
            value={formatMetricCount(metrics, 'num_only_per_system')}
            icon={Eye}
            accent={hiddenCount != null && hiddenCount > 0 ? 'yellow' : 'blue'}
          />
        </div>
      </div>

      <div>
        <h4 className="mb-3 text-sm font-semibold text-blueprint-blue">ML vs WPS overlap</h4>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <OverlapCard
            label="WPS only"
            helpKey="anomaly_overlap_rule_only"
            value={formatMetricCount(metrics, 'num_only_rule_based')}
          />
          <OverlapCard
            label="Both agree"
            helpKey="anomaly_overlap_both"
            value={formatMetricCount(metrics, 'num_rule_and_per_system')}
          />
          <OverlapCard
            label="ML only"
            helpKey="anomaly_overlap_ml_only"
            value={formatMetricCount(metrics, 'num_only_per_system')}
            highlight={hiddenCount != null && hiddenCount > 0}
          />
          <OverlapCard
            label="All-system ML"
            helpKey="num_global"
            value={formatMetricCount(metrics, 'num_global')}
          />
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 text-heritage-grey" />
          <h4 className="text-sm font-semibold text-blueprint-blue">Proxy validation</h4>
        </div>
        <AnalyticsDataTable
          columns={proxyColumns}
          data={proxyRows}
          rowKey={(r) => r.level}
          emptyMessage="Proxy validation metrics not available."
        />
      </div>

      {(hiddenCount ?? 0) > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-[#E6A100]/30 bg-[rgba(230,161,0,0.06)] px-4 py-3 text-sm text-[#8A6500]">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {formatCount(hiddenCount!)} hidden issues — flagged by ML but did not fail WPS.
            Review these for possible process problems.
          </span>
        </div>
      )}
    </div>
  );
};
